import { useEffect, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { useMutation } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import { updateBulletin } from 'services/fund'

/** The create sheet's own default, for a post whose sticker is gone. */
const DEFAULT_STICKER = 3

interface EditBulletinModalProps {
    /** The post being edited; `null` keeps the sheet closed. */
    post: IBulletiens | null
    moneyPool: string | undefined
    onHide: () => void
    refetch: () => void
}

/**
 * «ویرایش اعلان» on the fund's board (#132). The cashbox board and Android
 * could already edit a post; the moneypool board could only delete one and
 * post it again, which told every member twice.
 *
 * The members are told about an edit on Bale/Eitaa, push and «پیام‌ها», but
 * never by SMS, and only when the text really changed — the backend compares
 * the two texts, and a save that changes nothing is also blocked here, so
 * «ذخیره» cannot be pressed to re-send a post.
 */
export default function EditBulletinModal({
    post,
    moneyPool,
    onHide,
    refetch,
}: EditBulletinModalProps) {
    const [value, setValue] = useState('')

    // A different post opened on the same sheet starts from its own text.
    useEffect(() => {
        setValue(post?.text ?? '')
    }, [post])

    const original = (post?.text ?? '').trim()
    const next = value.trim()
    const changed = next.length > 0 && next !== original

    const save = useMutation(updateBulletin, {
        onSuccess: () => {
            posthog.capture('bulletin_edit_succeeded', {
                fund_id: Number(moneyPool),
                bulletin_id: post?.id,
            })
            toast.success('اعلان ویرایش شد')
            refetch()
            onHide()
        },
        onError: (error: { response?: { status?: number } }) => {
            posthog.capture('bulletin_edit_failed', {
                fund_id: Number(moneyPool),
                bulletin_id: post?.id,
                status: error?.response?.status ?? null,
            })
            toast.error('ویرایش اعلان انجام نشد. دوباره تلاش کنید.')
        },
    })

    const submit = () => {
        if (!post || !moneyPool || !changed || save.isLoading) return
        posthog.capture('bulletin_edit_submitted', {
            fund_id: Number(moneyPool),
            bulletin_id: post.id,
            // never the text itself
            length_before: original.length,
            length_after: next.length,
        })
        save.mutate({
            moneypoolId: moneyPool,
            id: post.id,
            text: next,
            sticker: post.sticker?.id ?? DEFAULT_STICKER,
        })
    }

    return (
        <Modal
            show={Boolean(post)}
            onHide={onHide}
            size="sm"
            aria-labelledby="edit-bulletin-title"
            centered
        >
            <div className=" w-full py-4 text-center text-xs">
                <p id="edit-bulletin-title" className=" my-1 font-bold">
                    ویرایش اعلان
                </p>
                <p>(حداکثر پانصد حرف)</p>
                <hr className=" my-2" />
                <textarea
                    value={value}
                    maxLength={500}
                    onChange={(e) => setValue(e.target.value)}
                    aria-label="متن اعلان"
                    className=" h-32 w-full text-center outline-none"
                />
                <hr className=" my-2" />
                <p
                    className=" mx-3 mb-2 rounded-lg bg-brand-soft p-3 text-right leading-6 text-text-body"
                    data-testid="bulletin-edit-reach"
                >
                    ✏️ متن تازه در بله یا ایتا و داخل همیان به اعضا خبر داده
                    می‌شود. برای ویرایش پیامک فرستاده نمی‌شود.
                </p>
                <button
                    type="button"
                    className=" text-xs text-current disabled:opacity-40"
                    disabled={!changed || save.isLoading}
                    onClick={submit}
                >
                    {save.isLoading ? 'در حال ذخیره…' : 'ذخیرهٔ ویرایش'}
                </button>
                <hr className=" my-2" />
                <button type="button" className=" text-xs" onClick={onHide}>
                    انصراف
                </button>
            </div>
        </Modal>
    )
}
